// statistics_clinic_06_practice_hypothesis_ab_testing.md — DIY A/B test
// cases. Some land as "could be chance" (|t| < 2) and some as "likely real"
// (|t| > 2) — a deliberately realistic mix, not "every test finds an effect."

export const STATS_CLINIC_06_TRACKER_ID = 'statistics-clinic-06-practice-bank';

export const STATS_CLINIC_06_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_06_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Email subject line test',
    scenario: 'A marketing analyst tested two subject lines for the same email campaign, splitting the send list randomly. Leadership wants to know whether the new subject line genuinely improved open rates before it becomes the new default.',
    tables: [{
      columns: ['group', 'mean_open_rate_pct', 'sd_pct', 'n_sends'],
      rows: [['Control (A)', '18.0', '5.0', '40'], ['Variant (B)', '19.5', '4.8', '40']],
    }],
    answerGuide: {
      business_question: 'Did the new subject line genuinely improve open rates?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'Comparing two group means with known spread and sample size calls for a t-statistic, not the raw 1.5-point gap alone.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(5.0^2/40 + 4.8^2/40) = sqrt(0.625+0.576) = sqrt(1.201) = 1.10. t = (19.5-18.0)/1.10 = 1.5/1.10 = 1.37.',
      result: '|t| = 1.37, below the |t| > 2 rule of thumb.',
      meaning: 'The 1.5 percentage-point gap is small relative to natural day-to-day variation — this could plausibly be chance.',
      decision: 'Do not roll out the new subject line as "proven better" yet — either run the test longer to collect more data, or treat the result as inconclusive.',
      caution: '|t| < 2 does not prove there is no effect, only that this sample was not large enough to be confident there is one.',
    },
    ruleTerms: {
      business_question: ['open rate', 'genuinely improve'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['spread', 'sample size'],
      calculation: ['1.10', '1.37'],
      result: ['1.37', 'below'],
      meaning: ['small', 'chance'],
      decision: ['do not roll out', 'inconclusive'],
      caution: ['does not prove', 'no effect'],
    },
    criticalTerms: { calculation: ['1.37'], result: ['1.37'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: App onboarding UI test',
    scenario: "A product team redesigned the app's onboarding flow to reduce the time it takes a new user to complete their first task. Before rolling the new design out to everyone, they want to know whether the observed speed-up is a real improvement or could just be normal variation between users.",
    tables: [{
      columns: ['group', 'mean_time_seconds', 'sd_seconds', 'n_users'],
      rows: [['Control (A, old UI)', '45', '10', '30'], ['Variant (B, new UI)', '38', '9', '30']],
    }],
    answerGuide: {
      business_question: 'Is the new onboarding UI a genuinely faster experience?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'Comparing two group means with known spread and sample size calls for a t-statistic, not the raw 7-second gap alone.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(10^2/30 + 9^2/30) = sqrt(3.333+2.7) = sqrt(6.033) = 2.46. t = (38-45)/2.46 = -7/2.46 = -2.85.',
      result: '|t| = 2.85, above the |t| > 2 rule of thumb.',
      meaning: 'The 7-second speed-up is larger than natural variation between users would explain — this looks like a real improvement.',
      decision: 'Reject H0 and roll out the new onboarding UI, since the speed-up looks genuine.',
      caution: 'A real effect does not automatically mean a business-critical one — check whether 7 seconds meaningfully changes onboarding completion or drop-off before over-selling the result.',
    },
    ruleTerms: {
      business_question: ['faster', 'genuinely'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['spread', 'sample size'],
      calculation: ['2.46', '-2.85', '2.85'],
      result: ['2.85', 'above'],
      meaning: ['larger', 'real improvement'],
      decision: ['reject', 'roll out'],
      caution: ['business-critical', 'meaningfully'],
    },
    criticalTerms: { calculation: ['2.85'], result: ['2.85'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Signup page layout test',
    scenario: 'A growth analyst tested a redesigned signup page layout against the current one, randomly splitting new visitors between the two. Before recommending a full rollout, they want an honest read on whether the small improvement they observed is real.',
    tables: [{
      columns: ['group', 'mean_signup_rate_pct', 'sd_pct', 'n_days'],
      rows: [['Control (A)', '12.0', '3.0', '60'], ['Variant (B)', '12.8', '3.2', '60']],
    }],
    answerGuide: {
      business_question: 'Is the redesigned signup page a real improvement?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'Comparing two group means with known spread and sample size calls for a t-statistic, not the raw 0.8-point gap alone.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(3.0^2/60 + 3.2^2/60) = sqrt(0.15+0.1707) = sqrt(0.3207) = 0.57. t = (12.8-12.0)/0.57 = 0.8/0.57 = 1.41.',
      result: '|t| = 1.41, below the |t| > 2 rule of thumb.',
      meaning: 'The 0.8 percentage-point gap is small relative to natural day-to-day variation — this could plausibly be chance.',
      decision: 'Do not claim the redesign is proven better yet — either extend the test to collect more data, or treat it as inconclusive.',
      caution: 'A small, honest "inconclusive" verdict is more useful to the business than overselling a difference the data cannot yet support.',
    },
    ruleTerms: {
      business_question: ['real improvement', 'signup'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['spread', 'sample size'],
      calculation: ['0.57', '1.41'],
      result: ['1.41', 'below'],
      meaning: ['small', 'chance'],
      decision: ['do not claim', 'inconclusive'],
      caution: ['honest', 'overselling'],
    },
    criticalTerms: { calculation: ['1.41'], result: ['1.41'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Aldi self-checkout express lane test',
    scenario: 'Aldi wants to know whether a dedicated self-checkout express lane for baskets with 15 items or fewer reduces checkout time during peak trading hours. Similar stores are split between the current checkout setup and the new express-lane setup.',
    tables: [{
      columns: ['group', 'mean_checkout_time_minutes', 'sd_minutes', 'n_customers'],
      rows: [['Control (A, current setup)', '7.8', '2.4', '1000'], ['Variant (B, express lane)', '6.9', '2.1', '1000']],
    }],
    answerGuide: {
      business_question: 'Did the express self-checkout lane genuinely reduce checkout time?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'Comparing two independent group means while accounting for spread and sample size calls for a t-statistic.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(2.4^2/1000 + 2.1^2/1000) = sqrt(0.00576+0.00441) = sqrt(0.01017) = 0.10. t = (6.9-7.8)/0.10 = -8.93.',
      result: '|t| = 8.93, well above the |t| > 2 rule of thumb.',
      meaning: 'The shorter checkout time is far larger than expected random variation; the express lane looks genuinely faster.',
      decision: 'Reject H0 and consider rolling out the express lane if staffing and equipment costs are justified.',
      caution: 'Lower is better for this metric, so the negative t-statistic is a good direction; still monitor basket abandonment and checkout error rates.',
    },
    ruleTerms: {
      business_question: ['checkout time', 'reduce'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['spread', 'sample size'],
      calculation: ['0.10', '-8.93', '8.93'],
      result: ['8.93', 'above'],
      meaning: ['faster', 'variation'],
      decision: ['reject', 'roll out'],
      caution: ['lower is better', 'error rates'],
    },
    criticalTerms: { calculation: ['8.93'], result: ['8.93'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Aldi shelf label promotion test',
    scenario: 'Aldi tests whether adding "Best Value This Week" shelf labels to selected own-brand products increases daily unit sales. Matched stores are split between normal shelf labels and the promotional shelf labels.',
    tables: [{
      columns: ['group', 'mean_units_sold_per_day', 'sd_units', 'n_store_days'],
      rows: [['Control (A, normal labels)', '42', '10', '30'], ['Variant (B, value labels)', '49', '11', '30']],
    }],
    answerGuide: {
      business_question: 'Did the "Best Value This Week" shelf labels genuinely increase unit sales?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'The decision compares average daily units sold across two independent store-day groups.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(10^2/30 + 11^2/30) = sqrt(3.333+4.033) = sqrt(7.367) = 2.71. t = (49-42)/2.71 = 2.58.',
      result: '|t| = 2.58, above the |t| > 2 rule of thumb.',
      meaning: 'The sales uplift looks statistically credible, not just random store-day variation.',
      decision: 'Reject H0 and expand the labels to more products, subject to checking margin and stock availability.',
      caution: 'Higher units sold may still be bad commercially if the label shifts customers from higher-margin alternatives.',
    },
    ruleTerms: {
      business_question: ['shelf labels', 'unit sales'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['daily units', 'store-day'],
      calculation: ['2.71', '2.58'],
      result: ['2.58', 'above'],
      meaning: ['credible', 'variation'],
      decision: ['reject', 'expand'],
      caution: ['margin', 'higher-margin'],
    },
    criticalTerms: { calculation: ['2.58'], result: ['2.58'] },
  },
  {
    id: 'DIY06',
    title: 'DIY Practice 06: African grocery WhatsApp pre-order test',
    scenario: 'An African grocery shop sells hot food at lunchtime and queues build up around jollof rice, stew, and plantain orders. The owner tests whether WhatsApp pre-ordering for pickup reduces customer wait time.',
    tables: [{
      columns: ['group', 'mean_wait_time_minutes', 'sd_minutes', 'n_customers'],
      rows: [['Control (A, order at counter)', '14', '6', '35'], ['Variant (B, WhatsApp pre-order)', '10', '5', '35']],
    }],
    answerGuide: {
      business_question: 'Did WhatsApp pre-ordering genuinely reduce hot-food wait time?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'Wait time is a numeric mean, and the shop is comparing two independent customer groups.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(6^2/35 + 5^2/35) = sqrt(1.029+0.714) = sqrt(1.743) = 1.32. t = (10-14)/1.32 = -3.03.',
      result: '|t| = 3.03, above the |t| > 2 rule of thumb.',
      meaning: 'The 4-minute reduction looks larger than normal customer-to-customer variation; the pre-order system appears to work.',
      decision: 'Reject H0 and keep the WhatsApp pre-order process, while improving order tracking if volume grows.',
      caution: 'Lower wait time is good here, but the shop should also track missed orders, staff workload, and customer satisfaction.',
    },
    ruleTerms: {
      business_question: ['wait time', 'pre-order'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['numeric mean', 'customer groups'],
      calculation: ['1.32', '-3.03', '3.03'],
      result: ['3.03', 'above'],
      meaning: ['4-minute', 'appears to work'],
      decision: ['reject', 'keep'],
      caution: ['missed orders', 'staff workload'],
    },
    criticalTerms: { calculation: ['3.03'], result: ['3.03'] },
  },
  {
    id: 'DIY07',
    title: 'DIY Practice 07: African grocery meal bundle display test',
    scenario: 'An African grocery shop tests whether placing a jollof rice bundle display near rice, oil, tomatoes, and seasoning increases average basket value. The owner compares matched trading days with and without the display.',
    tables: [{
      columns: ['group', 'mean_basket_value_gbp', 'sd_gbp', 'n_days'],
      rows: [['Control (A, standard layout)', '22', '8', '28'], ['Variant (B, bundle display)', '25', '9', '28']],
    }],
    answerGuide: {
      business_question: 'Did the jollof rice bundle display genuinely increase basket value?',
      statistic_family: 'Hypothesis testing.',
      statistic_chosen: 't-statistic (A/B test).',
      why_statistic: 'The owner is comparing average basket value across two groups of trading days, so the raw GBP 3 gap needs to be judged against variation.',
      calculation: 'H0: no real effect. H1: real effect. SE = sqrt(8^2/28 + 9^2/28) = sqrt(2.286+2.893) = sqrt(5.179) = 2.28. t = (25-22)/2.28 = 1.32.',
      result: '|t| = 1.32, below the |t| > 2 rule of thumb.',
      meaning: 'The GBP 3 increase is promising but could plausibly be chance.',
      decision: 'Do not claim the display is proven to increase basket value yet; extend the test across more comparable days or refine the bundle.',
      caution: 'Sales may vary by payday, weather, events, or stock availability, so matched days and clean randomisation matter.',
    },
    ruleTerms: {
      business_question: ['basket value', 'bundle display'],
      statistic_family: ['hypothesis testing'],
      statistic_chosen: ['t-statistic', 't statistic'],
      why_statistic: ['basket value', 'variation'],
      calculation: ['2.28', '1.32'],
      result: ['1.32', 'below'],
      meaning: ['promising', 'chance'],
      decision: ['do not claim', 'extend'],
      caution: ['payday', 'stock availability'],
    },
    criticalTerms: { calculation: ['1.32'], result: ['1.32'] },
  },
];

export const STATS_CLINIC_06_CONFIG = {
  trackerId: STATS_CLINIC_06_TRACKER_ID,
  resourceKey: 'statistics_clinic_06_practice_hypothesis_ab_testing',
  rubricKey: 'statistics_hypothesis_ab_testing',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Hypothesis Testing & A/B Test Cases',
  description: 'Each case gives two A/B test groups. State H0/H1, compute the t-statistic, then decide whether the difference looks real or could be chance. Use the worked solution guide to check each calculation step after your attempt.',
  introAlert: 'A large |t| (rule of thumb: greater than 2) suggests a real effect. A small |t| means the difference could plausibly be chance - that is a valid, honest verdict too. Open the worked solution if learners need the SE and t-statistic unpacked.',
  decisionFramework: {
    title: 'Flagged decision framework: A/B test findings',
    description: 'The decision is based on the size of the effect compared with uncertainty, not on whether variant B simply looks bigger.',
    rules: [
      '|t| below 2: call the result inconclusive; do not roll out as proven better.',
      '|t| around or above 2: say the effect looks statistically credible, then check whether the size matters operationally.',
      'Negative t-statistic: interpret the direction in business terms; lower can be better for time, errors, or cost.',
      'Every response must separate statistical confidence from practical value.',
    ],
    responseFrame: 'Frame the response as: "H0 says [no effect] and H1 says [real effect]. The t-statistic is [value], so this is [inconclusive / likely real]. I would [extend the test / roll out / monitor], but I would not overclaim because [sample size, effect size, or business value risk]."',
  },
  lenses: STATS_CLINIC_06_LENSES,
  workingField: null,
  items: STATS_CLINIC_06_ITEMS,
};
